import React, { useState, useMemo } from 'react';
import { cricketFlagEmoji } from '../../utils/countryFlags';
import {
  Dialog,
  Button,
  Box,
  Typography,
  Chip,
  Avatar,
  ButtonBase,
  IconButton,
  InputAdornment,
  TextField,
  useMediaQuery,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  SwapHoriz,
  AutoAwesome,
  Close,
  Search,
  CheckCircle,
  ChevronRight,
  People,
  EmojiEvents,
  ArrowForward,
} from '@mui/icons-material';
import { colors } from '../../theme/colors';
import type { League, Player, LeagueSquad, SquadPlayer } from '../../types/database';

interface TransferModalProps {
  open: boolean;
  onClose: () => void;
  league: League;
  existingSquad: LeagueSquad;
  availablePlayers: Player[];
  onSubmitTransfer: (transferData: TransferData) => Promise<void>;
  /**
   * When set, the modal is being driven by an admin on a squad owner's behalf.
   * It only changes the header copy so the admin can verify whose squad they're
   * editing — the transfer logic and audit stamping are handled by the caller.
   */
  adminContext?: { actingUid: string; targetUserName?: string };
}

export interface TransferData {
  transferType: 'bench' | 'flexible' | 'midSeason' | 'wildcard';
  changeType: 'playerSubstitution' | 'roleReassignment';

  // Player Substitution fields
  playerOut?: string; // player ID to remove
  playerIn?: string; // player ID to add

  // Role Reassignment fields
  newCaptainId?: string; // Only available for bench transfers
  newViceCaptainId?: string;
  newXFactorId?: string;
}

// ── Style helpers ─────────────────────────────────────────────────────────────

const accent = colors.blue.electric;

const ctaButton = (disabled?: boolean) => ({
  bgcolor: disabled ? alpha(accent, 0.25) : accent,
  color: '#fff',
  textTransform: 'none' as const,
  fontWeight: 700,
  fontFamily: "'Satoshi', sans-serif",
  borderRadius: 2.5,
  fontSize: '0.92rem',
  letterSpacing: '0.01em',
  boxShadow: disabled ? 'none' : `0 4px 16px ${alpha(accent, 0.35)}`,
  transition: 'all 0.18s ease',
  '&:hover:not(:disabled)': {
    bgcolor: accent,
    filter: 'brightness(1.12)',
    boxShadow: `0 6px 20px ${alpha(accent, 0.45)}`,
    transform: 'translateY(-1px)',
  },
  '&:active:not(:disabled)': { transform: 'translateY(0)', filter: 'brightness(0.96)' },
  '&.Mui-disabled': { bgcolor: alpha(accent, 0.18), color: alpha('#fff', 0.35), boxShadow: 'none' },
});

// ── Main Component ────────────────────────────────────────────────────────────

type CricketSubStep = 'changeType' | 'playerOut' | 'playerIn' | 'roleReassign';

const TransferModal: React.FC<TransferModalProps> = ({
  open,
  onClose,
  league,
  existingSquad,
  availablePlayers,
  onSubmitTransfer,
  adminContext
}) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  // Admin-on-behalf mode: bypass the live change toggles and the mid-season
  // window so an admin can act even when transfers are closed (e.g. site down).
  // The transfer types the league offers and the remaining counts are still
  // respected — only the live gating is lifted.
  const isAdmin = Boolean(adminContext);

  const [activeStep, setActiveStep] = useState(0);
  const [selectedTransferType, setSelectedTransferType] = useState<'bench' | 'flexible' | 'midSeason' | 'wildcard' | null>(null);
  const [selectedChangeType, setSelectedChangeType] = useState<'playerSubstitution' | 'roleReassignment' | null>(null);
  const [playerOut, setPlayerOut] = useState<string>('');
  const [playerIn, setPlayerIn] = useState<string>('');
  const [newCaptain, setNewCaptain] = useState<string>('');
  const [newViceCaptain, setNewViceCaptain] = useState<string>('');
  const [newXFactor, setNewXFactor] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [playerInSearch, setPlayerInSearch] = useState('');
  const [subStep, setSubStep] = useState<CricketSubStep>('changeType');
  const [activeRole, setActiveRole] = useState<'C' | 'VC' | 'X'>('VC');

  // ── Logic (untouched) ──────────────────────────────────────────────────────

  const getAvailableTransfers = () => {
    const transfers = league.transferTypes;
    if (!transfers) return { bench: 0, flexible: 0, midSeason: 0, wildcard: 0 };

    const benchUsed = existingSquad.benchTransfersUsed || 0;
    const flexibleUsed = existingSquad.flexibleTransfersUsed || 0;
    const midSeasonUsed = existingSquad.midSeasonTransfersUsed || 0;
    const wildcardUsed = existingSquad.wildcardTransfersUsed || 0;

    return {
      bench: transfers.benchTransfers.enabled ? Math.max(0, transfers.benchTransfers.maxAllowed - benchUsed) : 0,
      flexible: transfers.flexibleTransfers.enabled ? Math.max(0, transfers.flexibleTransfers.maxAllowed - flexibleUsed) : 0,
      midSeason: transfers.midSeasonTransfers.enabled ? Math.max(0, transfers.midSeasonTransfers.maxAllowed - midSeasonUsed) : 0,
      wildcard: (transfers.wildcardTransfers?.enabled) ? Math.max(0, transfers.wildcardTransfers.maxAllowed - wildcardUsed) : 0,
    };
  };

  const availableTransfers = getAvailableTransfers();

  const isMidSeasonWindowOpen = () => {
    if (!league.transferTypes?.midSeasonTransfers.enabled) return false;
    const now = new Date();
    const windowStart = new Date(league.transferTypes.midSeasonTransfers.windowStartDate);
    const windowEnd = new Date(league.transferTypes.midSeasonTransfers.windowEndDate);
    return now >= windowStart && now <= windowEnd;
  };

  const handleNext = () => {
    setError('');

    if (activeStep === 0 && !selectedTransferType) {
      setError('Please select a transfer type to continue');
      return;
    }

    if (activeStep === 1 && !selectedChangeType) {
      setError('Please choose what you want to change');
      return;
    }

    if (activeStep === 1) {
      if (selectedChangeType === 'playerSubstitution') {
        if (!playerOut || !playerIn) {
          setError('Select both: which player to remove and which player to bring in');
          return;
        }
        const validationError = validatePlayerSubstitution();
        if (validationError) {
          setError(validationError);
          return;
        }
      } else if (selectedChangeType === 'roleReassignment') {
        const rolesSelected = [newCaptain, newViceCaptain, newXFactor].filter(Boolean).length;

        if (rolesSelected === 0) {
          setError('Select ONE role to reassign (Captain, Vice-Captain, or X-Factor)');
          return;
        }

        if (rolesSelected > 1) {
          setError('Only ONE role can be changed per transfer. Please deselect the others.');
          return;
        }

        if ((selectedTransferType === 'flexible' || selectedTransferType === 'midSeason') && newCaptain) {
          setError(`Captain can only be changed with a Bench or Wildcard transfer. You have ${availableTransfers.bench} bench and ${availableTransfers.wildcard} wildcard transfer${availableTransfers.wildcard !== 1 ? 's' : ''} available.`);
          return;
        }
      }
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedTransferType(null);
    setSelectedChangeType(null);
    setPlayerOut('');
    setPlayerIn('');
    setNewCaptain('');
    setNewViceCaptain('');
    setNewXFactor('');
    setError('');
    setPlayerInSearch('');
    setSubStep('changeType');
    setActiveRole('VC');
  };

  const validatePlayerSubstitution = (): string | null => {
    const playerOutData = existingSquad.players.find(p => p.playerId === playerOut);
    if (!playerOutData) return 'Player not found in your squad';

    if (selectedTransferType === 'flexible' || selectedTransferType === 'midSeason') {
      if (playerOut === existingSquad.captainId) {
        const playerName = playerOutData.playerName;
        return `${playerName} is your Captain. Captain cannot be removed with this transfer type. Use a Bench or Wildcard transfer, or change Captain first.`;
      }
    }

    if (selectedTransferType === 'bench') {
      const benchPlayers = existingSquad.players.filter((p, index) => index >= league.squadSize);
      const benchPlayerIds = benchPlayers.map(p => p.playerId);
      if (!benchPlayerIds.includes(playerIn)) {
        return 'Bench transfers can only bring in players from your bench. Choose a bench player.';
      }
    }

    if (selectedTransferType === 'wildcard') {
      const squadPlayerIds = existingSquad.players.map(p => p.playerId);
      if (squadPlayerIds.includes(playerIn)) {
        return 'Wildcard transfers can only bring in players from the pool (not current squad members).';
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError('');

      const transferData: TransferData = {
        transferType: selectedTransferType!,
        changeType: selectedChangeType!,
        playerOut: selectedChangeType === 'playerSubstitution' ? playerOut : undefined,
        playerIn: selectedChangeType === 'playerSubstitution' ? playerIn : undefined,
        newCaptainId: selectedChangeType === 'roleReassignment' ? newCaptain || undefined : undefined,
        newViceCaptainId: selectedChangeType === 'roleReassignment' ? newViceCaptain || undefined : undefined,
        newXFactorId: selectedChangeType === 'roleReassignment' ? newXFactor || undefined : undefined
      };

      await onSubmitTransfer(transferData);
      handleReset();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to submit transfer');
    } finally {
      setSubmitting(false);
    }
  };

  const getPlayerName = (playerId: string | undefined): string => {
    if (!playerId) return '';
    const player = existingSquad.players.find(p => p.playerId === playerId);
    return player?.playerName || playerId;
  };

  const getAvailablePlayersForSelection = (): Player[] => {
    if (selectedTransferType === 'bench') {
      const benchPlayersSquad = existingSquad.players.filter((p, index) => index >= league.squadSize);
      const benchPlayers = benchPlayersSquad.map(benchPlayer => {
        return availablePlayers.find(p => p.id === benchPlayer.playerId);
      }).filter((p): p is Player => p !== undefined);
      return benchPlayers;
    }

    if (selectedTransferType === 'wildcard') {
      const squadPlayerIds = existingSquad.players.map(p => p.playerId);
      return availablePlayers.filter(p => !squadPlayerIds.includes(p.id));
    }

    const squadPlayerIds = existingSquad.players.map(p => p.playerId);

    const benchPlayersSquad = existingSquad.players.filter((p, index) => index >= league.squadSize);
    const benchPlayers = benchPlayersSquad.map(benchPlayer => {
      return availablePlayers.find(p => p.id === benchPlayer.playerId);
    }).filter((p): p is Player => p !== undefined);

    const poolPlayers = availablePlayers.filter(p => !squadPlayerIds.includes(p.id));

    return [...benchPlayers, ...poolPlayers];
  };

  const getPlayersForRemoval = (): SquadPlayer[] => {
    const mainSquadPlayers = existingSquad.players.slice(0, league.squadSize);

    if (selectedTransferType === 'flexible' || selectedTransferType === 'midSeason') {
      return mainSquadPlayers.filter(p =>
        p.playerId !== existingSquad.captainId
      );
    } else if (selectedTransferType === 'bench' || selectedTransferType === 'wildcard') {
      return mainSquadPlayers;
    }

    return mainSquadPlayers;
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  const filteredPlayersIn = useMemo(() => {
    const pool = getAvailablePlayersForSelection();
    if (!playerInSearch) return pool;
    const q = playerInSearch.toLowerCase();
    return pool.filter(p =>
      p.name.toLowerCase().includes(q) || (p.team ?? '').toLowerCase().includes(q)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerInSearch, selectedTransferType, existingSquad, availablePlayers]);

  // ── Inline components ──────────────────────────────────────────────────────

  const StepHeader = ({ title, subtitle }: { title: string; subtitle?: string }) => (
    <Box sx={{
      px: { xs: 2, sm: 2.5 }, pt: { xs: 2.25, sm: 2.5 }, pb: 1.75,
      borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0,
      position: 'relative',
    }}>
      <Box sx={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '1.5px', borderRadius: '0 0 4px 4px', bgcolor: alpha(accent, 0.45) }} />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box>
          <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: { xs: '1rem', sm: '1.1rem' }, color: colors.text.primary, lineHeight: 1.3 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: '0.7rem', color: alpha(colors.text.secondary, 0.5), mt: 0.3, lineHeight: 1.4 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose}
          sx={{ color: alpha(colors.text.secondary, 0.4), mt: -0.25, flexShrink: 0,
            '&:hover': { color: colors.text.primary, bgcolor: alpha('#fff', 0.06) } }}>
          <Close sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>
    </Box>
  );

  interface TransferTypeCardProps {
    label: string;
    icon: React.ReactNode;
    bullets: [string, string];
    count: number;
    selected: boolean;
    disabled: boolean;
    onClick: () => void;
    gold?: boolean;
  }
  const TransferTypeCard = ({ label, icon, bullets, count, selected, disabled, onClick, gold }: TransferTypeCardProps) => {
    const a = gold ? '#FFD700' : accent;
    return (
      <ButtonBase
        onClick={onClick}
        disabled={disabled}
        sx={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 1.5,
          px: 1.5, py: 1.4, mb: 1, borderRadius: 2.5,
          border: `1px solid ${selected ? alpha(a, 0.65) : alpha(a, 0.18)}`,
          bgcolor: selected ? alpha(a, 0.1) : 'transparent',
          opacity: disabled ? 0.38 : 1,
          transition: 'all 0.15s ease',
          textAlign: 'left',
          '&:hover:not(:disabled)': { bgcolor: alpha(a, 0.07), border: `1px solid ${alpha(a, 0.4)}` },
          '&:active:not(:disabled)': { transform: 'scale(0.99)' },
        }}
      >
        {/* Icon badge */}
        <Box sx={{
          width: 40, height: 40, borderRadius: 2, flexShrink: 0,
          bgcolor: selected ? alpha(a, 0.2) : alpha(a, 0.08),
          border: `1.5px solid ${alpha(a, selected ? 0.65 : 0.25)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </Box>
        {/* Text block */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: '0.9rem', color: gold ? '#FFD700' : colors.text.primary, lineHeight: 1.25, mb: 0.5 }}>
            {label}
          </Typography>
          {bullets.map((b, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
              <Box component="span" sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: alpha(a, 0.5), flexShrink: 0, mt: '0.35em' }} />
              <Typography sx={{ fontSize: '0.67rem', color: alpha(colors.text.secondary, 0.6), lineHeight: 1.5 }}>
                {b}
              </Typography>
            </Box>
          ))}
        </Box>
        {/* Count + check */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 0.5 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: count > 0 ? a : alpha(colors.text.secondary, 0.35), fontFamily: "'Satoshi', sans-serif", lineHeight: 1 }}>
            {count}
          </Typography>
          <Typography sx={{ fontSize: '0.55rem', color: alpha(colors.text.secondary, 0.35), textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1 }}>
            left
          </Typography>
          {selected
            ? <CheckCircle sx={{ fontSize: 15, color: a, mt: 0.25 }} />
            : <ChevronRight sx={{ fontSize: 15, color: alpha(a, 0.35), mt: 0.25 }} />
          }
        </Box>
      </ButtonBase>
    );
  };

  interface PlayerCardProps {
    player: SquadPlayer;
    selected?: boolean;
    disabled?: boolean;
    badge?: 'C' | 'VC' | 'X';
    onClick: () => void;
  }
  const PlayerCard = ({ player, selected, disabled, badge, onClick }: PlayerCardProps) => {
    const badgeColor = badge === 'C' ? '#FF9800' : badge === 'VC' ? accent : '#9C27B0';
    return (
      <ButtonBase
        onClick={onClick}
        disabled={disabled}
        sx={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 1.25,
          px: 1.25, py: 1, borderRadius: 2.5, mb: 0.5,
          border: `1px solid ${selected ? alpha(accent, 0.55) : 'transparent'}`,
          bgcolor: selected ? alpha(accent, 0.1) : 'transparent',
          opacity: disabled ? 0.38 : 1,
          transition: 'all 0.13s ease',
          textAlign: 'left',
          '&:hover:not(:disabled)': { bgcolor: alpha(accent, 0.07), border: `1px solid ${alpha(accent, 0.25)}` },
          '&:active:not(:disabled)': { transform: 'scale(0.99)' },
        }}
      >
        <Avatar sx={{ width: 32, height: 32, fontSize: '0.82rem', fontWeight: 700, bgcolor: alpha(accent, 0.15), color: accent, flexShrink: 0 }}>
          {player.playerName.charAt(0)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: '0.87rem', color: colors.text.primary, lineHeight: 1.25 }} noWrap>
            {player.playerName}
          </Typography>
          <Typography sx={{ fontSize: '0.66rem', color: alpha(colors.text.secondary, 0.55), lineHeight: 1.3 }}>
            {cricketFlagEmoji(player.team)}{cricketFlagEmoji(player.team) ? ' ' : ''}{player.team} · {player.role}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {badge && (
            <Chip label={badge} size="small" sx={{
              height: 18, fontSize: '0.62rem', fontWeight: 700,
              bgcolor: alpha(badgeColor, 0.15), color: badgeColor,
              border: `1px solid ${alpha(badgeColor, 0.3)}`,
            }} />
          )}
          {selected
            ? <CheckCircle sx={{ fontSize: 16, color: accent }} />
            : <ChevronRight sx={{ fontSize: 16, color: alpha(accent, 0.35) }} />
          }
        </Box>
      </ButtonBase>
    );
  };

  interface PoolPlayerCardProps {
    player: Player;
    selected?: boolean;
    onClick: () => void;
  }
  const PoolPlayerCard = ({ player, selected, onClick }: PoolPlayerCardProps) => (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 1.25,
        px: 1.25, py: 1, borderRadius: 2.5, mb: 0.5,
        border: `1px solid ${selected ? alpha(accent, 0.55) : 'transparent'}`,
        bgcolor: selected ? alpha(accent, 0.1) : 'transparent',
        transition: 'all 0.13s ease',
        textAlign: 'left',
        '&:hover': { bgcolor: alpha(accent, 0.07), border: `1px solid ${alpha(accent, 0.25)}` },
        '&:active': { transform: 'scale(0.99)' },
      }}
    >
      <Avatar sx={{ width: 32, height: 32, fontSize: '0.82rem', fontWeight: 700, bgcolor: alpha(colors.success.primary, 0.15), color: colors.success.primary, flexShrink: 0 }}>
        {player.name.charAt(0)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: '0.87rem', color: colors.text.primary, lineHeight: 1.25 }} noWrap>
          {player.name}
        </Typography>
        <Typography sx={{ fontSize: '0.66rem', color: alpha(colors.text.secondary, 0.55), lineHeight: 1.3 }}>
          {cricketFlagEmoji(player.team)}{cricketFlagEmoji(player.team) ? ' ' : ''}{player.team} · {player.role}
        </Typography>
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        {selected
          ? <CheckCircle sx={{ fontSize: 16, color: accent }} />
          : <ChevronRight sx={{ fontSize: 16, color: alpha(accent, 0.35) }} />
        }
      </Box>
    </ButtonBase>
  );

  // ── Step renders ───────────────────────────────────────────────────────────

  const renderStepType = () => (
    <>
      <StepHeader
        title={adminContext ? `Admin Transfer — ${adminContext.targetUserName ?? 'on behalf'}` : 'Make a Transfer'}
        subtitle={adminContext ? 'Acting on behalf of squad owner' : 'Choose your transfer type'}
      />
      <Box sx={{ p: { xs: 2, sm: 2.5 }, flex: 1, overflowY: 'auto' }}>
        {error && (
          <Box sx={{ px: 1.25, py: 1, borderRadius: 2, bgcolor: alpha('#EF4444', 0.09), border: `1px solid ${alpha('#EF4444', 0.25)}`, mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#EF4444', fontFamily: "'Satoshi', sans-serif" }}>{error}</Typography>
          </Box>
        )}
        {isAdmin && (
          <Box sx={{ px: 1.25, py: 1, borderRadius: 2, bgcolor: alpha('#FFD700', 0.08), border: `1px solid ${alpha('#FFD700', 0.3)}`, mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.72rem', color: '#FFD700', fontFamily: "'Satoshi', sans-serif" }}>
              Admin override active — closed windows and disabled toggles are bypassed. Remaining transfer counts still apply.
            </Typography>
          </Box>
        )}
        {league.transferTypes?.benchTransfers.enabled && (isAdmin || league.benchChangesEnabled) && (
          <TransferTypeCard
            label="Bench Change"
            icon={<People sx={{ fontSize: 20, color: availableTransfers.bench > 0 ? accent : alpha(accent, 0.4) }} />}
            bullets={['Player substitution using your bench players', 'Role reassignment for any role: C, VC or X']}
            count={availableTransfers.bench}
            selected={selectedTransferType === 'bench'}
            disabled={availableTransfers.bench === 0}
            onClick={() => setSelectedTransferType('bench')}
          />
        )}
        {league.transferTypes?.flexibleTransfers.enabled && (isAdmin || league.flexibleChangesEnabled) && (
          <TransferTypeCard
            label="Flexible Change"
            icon={<SwapHoriz sx={{ fontSize: 20, color: availableTransfers.flexible > 0 ? accent : alpha(accent, 0.4) }} />}
            bullets={['Player substitution from pool or bench (not Captain)', 'Role reassignment for Vice-Captain or X-Factor']}
            count={availableTransfers.flexible}
            selected={selectedTransferType === 'flexible'}
            disabled={availableTransfers.flexible === 0}
            onClick={() => setSelectedTransferType('flexible')}
          />
        )}
        {league.transferTypes?.wildcardTransfers?.enabled && (isAdmin || league.wildcardChangesEnabled) && (
          <TransferTypeCard
            label="Wildcard"
            icon={<AutoAwesome sx={{ fontSize: 20, color: '#FFD700' }} />}
            bullets={['Player substitution with all roles, including Captain', 'Role reassignment for any role: C, VC or X']}
            count={availableTransfers.wildcard}
            selected={selectedTransferType === 'wildcard'}
            disabled={availableTransfers.wildcard === 0}
            onClick={() => setSelectedTransferType('wildcard')}
            gold
          />
        )}
        {league.transferTypes?.midSeasonTransfers.enabled && (() => {
          const midSeasonOpen = isAdmin || isMidSeasonWindowOpen();
          return (
            <TransferTypeCard
              label="Mid-Season Change"
              icon={<EmojiEvents sx={{ fontSize: 20, color: (midSeasonOpen && availableTransfers.midSeason > 0) ? accent : alpha(accent, 0.4) }} />}
              bullets={[
                midSeasonOpen ? 'Player substitution from pool or bench (not Captain)' : 'Window closed — not available right now',
                'Role reassignment for Vice-Captain or X-Factor',
              ]}
              count={availableTransfers.midSeason}
              selected={selectedTransferType === 'midSeason'}
              disabled={!midSeasonOpen || availableTransfers.midSeason === 0}
              onClick={() => setSelectedTransferType('midSeason')}
            />
          );
        })()}
      </Box>
      <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 3, sm: 2.5 }, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <Button fullWidth variant="contained" disabled={!selectedTransferType}
          onClick={() => { setError(''); setActiveStep(1); setSubStep('changeType'); }}
          sx={{ ...ctaButton(!selectedTransferType) }}>
          Continue
        </Button>
      </Box>
    </>
  );

  const renderStepChangeType = () => (
    <>
      <StepHeader
        title="What to Change?"
        subtitle={`${selectedTransferType === 'bench' ? 'Bench' : selectedTransferType === 'flexible' ? 'Flexible' : selectedTransferType === 'wildcard' ? 'Wildcard' : 'Mid-Season'} transfer`}
      />
      <Box sx={{ p: { xs: 2, sm: 2.5 }, flex: 1, overflowY: 'auto' }}>
        <ButtonBase
          onClick={() => { setSelectedChangeType('playerSubstitution'); setSubStep('playerOut'); }}
          sx={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 1.5,
            px: 1.5, py: 1.4, mb: 1, borderRadius: 2.5,
            border: `1px solid ${alpha(accent, 0.18)}`,
            bgcolor: alpha(accent, 0.04),
            transition: 'all 0.15s ease', textAlign: 'left',
            '&:hover': { bgcolor: alpha(accent, 0.09), border: `1px solid ${alpha(accent, 0.38)}` },
            '&:active': { transform: 'scale(0.99)' },
          }}
        >
          <Box sx={{ width: 40, height: 40, borderRadius: 2, flexShrink: 0, bgcolor: alpha(accent, 0.1), border: `1px solid ${alpha(accent, 0.25)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SwapHoriz sx={{ fontSize: 20, color: accent }} />
          </Box>
          <Box sx={{ flex: 1, textAlign: 'left' }}>
            <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: '0.9rem', color: colors.text.primary, lineHeight: 1.3 }}>
              Player Substitution
            </Typography>
            <Typography sx={{ fontSize: '0.67rem', color: alpha(colors.text.secondary, 0.5), mt: 0.2 }}>
              Replace a player in your squad with a new one
            </Typography>
          </Box>
          <ChevronRight sx={{ fontSize: 18, color: alpha(accent, 0.4), flexShrink: 0 }} />
        </ButtonBase>

        <ButtonBase
          onClick={() => { setSelectedChangeType('roleReassignment'); setSubStep('roleReassign'); }}
          sx={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 1.5,
            px: 1.5, py: 1.4, borderRadius: 2.5,
            border: `1px solid ${alpha(accent, 0.18)}`,
            bgcolor: alpha(accent, 0.04),
            transition: 'all 0.15s ease', textAlign: 'left',
            '&:hover': { bgcolor: alpha(accent, 0.09), border: `1px solid ${alpha(accent, 0.38)}` },
            '&:active': { transform: 'scale(0.99)' },
          }}
        >
          <Box sx={{ width: 40, height: 40, borderRadius: 2, flexShrink: 0, bgcolor: alpha(accent, 0.1), border: `1px solid ${alpha(accent, 0.25)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EmojiEvents sx={{ fontSize: 20, color: accent }} />
          </Box>
          <Box sx={{ flex: 1, textAlign: 'left' }}>
            <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: '0.9rem', color: colors.text.primary, lineHeight: 1.3 }}>
              Role Reassignment
            </Typography>
            <Typography sx={{ fontSize: '0.67rem', color: alpha(colors.text.secondary, 0.5), mt: 0.2 }}>
              {(selectedTransferType === 'bench' || selectedTransferType === 'wildcard')
                ? 'Change Captain, Vice-Captain, or X-Factor'
                : 'Change Vice-Captain or X-Factor'
              }
            </Typography>
          </Box>
          <ChevronRight sx={{ fontSize: 18, color: alpha(accent, 0.4), flexShrink: 0 }} />
        </ButtonBase>
      </Box>
      <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 3, sm: 2.5 }, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <Button fullWidth variant="text" onClick={handleBack}
          sx={{ color: alpha(colors.text.secondary, 0.6), textTransform: 'none', fontFamily: "'Satoshi', sans-serif", borderRadius: 2 }}>
          ← Back
        </Button>
      </Box>
    </>
  );

  const renderStepPlayerOut = () => {
    const playersForRemoval = getPlayersForRemoval();
    const transferLabel = selectedTransferType === 'bench' ? 'Bench' : selectedTransferType === 'flexible' ? 'Flexible' : selectedTransferType === 'wildcard' ? 'Wildcard' : 'Mid-Season';
    return (
      <>
        <StepHeader title="Who's Going Out?" subtitle={`${transferLabel} transfer`} />
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.5, flex: 1, overflowY: 'auto' }}>
          {playersForRemoval.map(player => {
            const badge = player.playerId === existingSquad.captainId ? 'C' :
              player.playerId === existingSquad.viceCaptainId ? 'VC' :
              player.playerId === existingSquad.xFactorId ? 'X' : undefined;
            return (
              <PlayerCard
                key={player.playerId}
                player={player}
                selected={playerOut === player.playerId}
                badge={badge as 'C' | 'VC' | 'X' | undefined}
                onClick={() => { setPlayerOut(player.playerId); setSubStep('playerIn'); }}
              />
            );
          })}
        </Box>
        <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 3, sm: 2.5 }, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <Button fullWidth variant="text" onClick={() => setSubStep('changeType')}
            sx={{ color: alpha(colors.text.secondary, 0.6), textTransform: 'none', fontFamily: "'Satoshi', sans-serif", borderRadius: 2 }}>
            ← Back
          </Button>
        </Box>
      </>
    );
  };

  const renderStepPlayerIn = () => {
    const outgoingName = getPlayerName(playerOut);
    return (
      <>
        <StepHeader title="Who's Coming In?" subtitle={`Replacing ${outgoingName}`} />
        {/* OUT banner */}
        <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 1.25, pb: 0.5, borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0 }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.25, py: 0.9, borderRadius: 2,
            bgcolor: alpha('#EF4444', 0.06), border: `1px solid ${alpha('#EF4444', 0.15)}`,
          }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#EF4444', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.7rem', color: alpha('#EF4444', 0.75), fontWeight: 600, fontFamily: "'Satoshi', sans-serif" }}>
              OUT:
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: colors.text.primary, fontFamily: "'Satoshi', sans-serif", flex: 1 }} noWrap>
              {outgoingName}
            </Typography>
          </Box>
        </Box>
        {/* Search */}
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1, flexShrink: 0 }}>
          <TextField
            fullWidth size="small" placeholder="Search by name or team…"
            value={playerInSearch} onChange={e => setPlayerInSearch(e.target.value)}
            autoFocus={!isMobile}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: alpha(colors.text.secondary, 0.35) }} /></InputAdornment>,
              endAdornment: playerInSearch ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setPlayerInSearch('')} sx={{ p: 0.3 }}>
                    <Close sx={{ fontSize: 13, color: alpha(colors.text.secondary, 0.5) }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: {
                bgcolor: alpha(accent, 0.04), borderRadius: 2, fontSize: '0.82rem',
                '& fieldset': { borderColor: alpha(accent, 0.15) },
                '&:hover fieldset': { borderColor: alpha(accent, 0.3) },
                '&.Mui-focused fieldset': { borderColor: alpha(accent, 0.55) },
              },
            }}
          />
          {playerInSearch && (
            <Typography sx={{ fontSize: '0.62rem', color: alpha(colors.text.secondary, 0.38), mt: 0.5, pl: 0.5 }}>
              {filteredPlayersIn.length} result{filteredPlayersIn.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>
        {/* Player list */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 1, sm: 1.5 }, pb: 1 }}>
          {filteredPlayersIn.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <SwapHoriz sx={{ fontSize: 28, color: alpha(colors.text.secondary, 0.18), mb: 1 }} />
              <Typography sx={{ color: alpha(colors.text.secondary, 0.35), fontSize: '0.8rem', fontFamily: "'Satoshi', sans-serif" }}>
                {playerInSearch ? `No results for "${playerInSearch}"` : 'No players available'}
              </Typography>
            </Box>
          ) : filteredPlayersIn.map(p => (
            <PoolPlayerCard
              key={p.id}
              player={p}
              selected={playerIn === p.id}
              onClick={() => setPlayerIn(p.id)}
            />
          ))}
        </Box>
        {/* Footer */}
        <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 3, sm: 2.5 }, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: 1 }}>
          <Button variant="text" onClick={() => { setSubStep('playerOut'); setPlayerIn(''); }}
            sx={{ flex: 1, color: alpha(colors.text.secondary, 0.6), textTransform: 'none', fontFamily: "'Satoshi', sans-serif", borderRadius: 2 }}>
            ← Back
          </Button>
          <Button variant="contained" disabled={!playerIn}
            onClick={handleNext}
            sx={{ flex: 2.5, ...ctaButton(!playerIn) }}>
            Review Transfer
          </Button>
        </Box>
      </>
    );
  };

  const renderStepRoleReassign = () => {
    const canChangeC = selectedTransferType === 'bench' || selectedTransferType === 'wildcard';
    const mainSquad = existingSquad.players.slice(0, league.squadSize);

    const currentHolder =
      activeRole === 'C' ? existingSquad.captainId :
      activeRole === 'VC' ? existingSquad.viceCaptainId :
      existingSquad.xFactorId;

    return (
      <>
        <StepHeader title="Change a Role" subtitle="Select exactly one role to reassign" />
        <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 1.25, pb: 0.5, flexShrink: 0 }}>
          {/* Role tabs */}
          <Box sx={{ display: 'flex', gap: 0.75, mb: 1.25 }}>
            {(canChangeC ? ['C', 'VC', 'X'] : ['VC', 'X']).map(r => {
              const rc = r === 'C' ? '#FF9800' : r === 'VC' ? accent : '#9C27B0';
              const isActive = activeRole === r;
              return (
                <ButtonBase
                  key={r}
                  onClick={() => { setActiveRole(r as 'C' | 'VC' | 'X'); setNewCaptain(''); setNewViceCaptain(''); setNewXFactor(''); }}
                  sx={{
                    px: 1.5, py: 0.6, borderRadius: 10,
                    border: `1px solid ${isActive ? alpha(rc, 0.65) : alpha(rc, 0.2)}`,
                    bgcolor: isActive ? alpha(rc, 0.15) : 'transparent',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: isActive ? rc : alpha(rc, 0.55) }}>
                    {r}
                  </Typography>
                </ButtonBase>
              );
            })}
          </Box>
          {/* Current holder context */}
          <Typography sx={{ fontSize: '0.67rem', color: alpha(colors.text.secondary, 0.5), mb: 0.5 }}>
            Current {activeRole === 'C' ? 'Captain' : activeRole === 'VC' ? 'Vice-Captain' : 'X-Factor'}:{' '}
            <Box component="span" sx={{ color: colors.text.primary, fontWeight: 600 }}>
              {getPlayerName(currentHolder)}
            </Box>
          </Typography>
        </Box>
        {error && (
          <Box sx={{ mx: { xs: 1.5, sm: 2 }, px: 1.25, py: 1, borderRadius: 2, bgcolor: alpha('#EF4444', 0.09), border: `1px solid ${alpha('#EF4444', 0.25)}`, mb: 0.5 }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#EF4444', fontFamily: "'Satoshi', sans-serif" }}>{error}</Typography>
          </Box>
        )}
        <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 1, sm: 1.5 }, pb: 1 }}>
          {mainSquad.map(player => {
            const isCurrentHolder = player.playerId === currentHolder;
            const existingBadge = player.playerId === existingSquad.captainId ? 'C' :
              player.playerId === existingSquad.viceCaptainId ? 'VC' :
              player.playerId === existingSquad.xFactorId ? 'X' : undefined;
            const isSelected =
              (activeRole === 'C' && newCaptain === player.playerId) ||
              (activeRole === 'VC' && newViceCaptain === player.playerId) ||
              (activeRole === 'X' && newXFactor === player.playerId);
            return (
              <PlayerCard
                key={player.playerId}
                player={player}
                selected={isSelected}
                disabled={isCurrentHolder}
                badge={existingBadge as 'C' | 'VC' | 'X' | undefined}
                onClick={() => {
                  setError('');
                  if (activeRole === 'C') { setNewCaptain(player.playerId); setNewViceCaptain(''); setNewXFactor(''); }
                  else if (activeRole === 'VC') { setNewViceCaptain(player.playerId); setNewCaptain(''); setNewXFactor(''); }
                  else { setNewXFactor(player.playerId); setNewCaptain(''); setNewViceCaptain(''); }
                }}
              />
            );
          })}
        </Box>
        <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 3, sm: 2.5 }, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: 1 }}>
          <Button variant="text" onClick={() => setSubStep('changeType')}
            sx={{ flex: 1, color: alpha(colors.text.secondary, 0.6), textTransform: 'none', fontFamily: "'Satoshi', sans-serif", borderRadius: 2 }}>
            ← Back
          </Button>
          <Button variant="contained"
            disabled={![newCaptain, newViceCaptain, newXFactor].some(Boolean)}
            onClick={handleNext}
            sx={{ flex: 2.5, ...ctaButton(![newCaptain, newViceCaptain, newXFactor].some(Boolean)) }}>
            Review Transfer
          </Button>
        </Box>
      </>
    );
  };

  const renderStepConfirm = () => {
    const transferLabel =
      selectedTransferType === 'bench' ? 'Bench Change' :
      selectedTransferType === 'flexible' ? 'Flexible Change' :
      selectedTransferType === 'wildcard' ? 'Wildcard' :
      'Mid-Season Change';

    const remainingAfter = (availableTransfers[selectedTransferType!] ?? 0) - 1;

    return (
      <>
        <StepHeader title="Confirm Transfer" subtitle="Review before locking in" />
        <Box sx={{ p: { xs: 2, sm: 2.5 }, flex: 1, overflowY: 'auto' }}>
          {/* Transfer type badge */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Chip
              label={transferLabel}
              size="small"
              sx={{
                background: selectedTransferType === 'wildcard' ? 'linear-gradient(135deg, #FFD700, #FF8C00)' : accent,
                color: selectedTransferType === 'wildcard' ? '#000' : 'white',
                fontWeight: 'bold', fontSize: '0.75rem', px: 1.5, height: 28,
                boxShadow: selectedTransferType === 'wildcard' ? '0 2px 12px rgba(255,215,0,0.4)' : 'none',
              }}
            />
          </Box>

          {selectedChangeType === 'playerSubstitution' ? (
            <Box sx={{ mb: 2 }}>
              {/* OUT row */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.25,
                px: 1.5, py: 1.25, borderRadius: '12px 12px 4px 4px',
                bgcolor: alpha('#EF4444', 0.07), border: `1px solid ${alpha('#EF4444', 0.18)}`,
                borderBottom: 'none',
              }}>
                <Box sx={{ width: 28, height: 28, borderRadius: 1.25, bgcolor: alpha('#EF4444', 0.12), border: `1px solid ${alpha('#EF4444', 0.25)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '0.5rem', fontWeight: 800, color: '#EF4444', textTransform: 'uppercase' }}>OUT</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: colors.text.primary, fontSize: '0.9rem' }} noWrap>
                    {getPlayerName(playerOut)}
                  </Typography>
                  {(() => {
                    const p = existingSquad.players.find(p => p.playerId === playerOut);
                    return p ? (
                      <Typography sx={{ fontSize: '0.65rem', color: alpha(colors.text.secondary, 0.55) }}>
                        {cricketFlagEmoji(p.team)}{cricketFlagEmoji(p.team) ? ' ' : ''}{p.team} · {p.role}
                      </Typography>
                    ) : null;
                  })()}
                </Box>
                {playerOut === existingSquad.captainId && <Chip label="C" size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha('#FF9800', 0.15), color: '#FF9800', border: `1px solid ${alpha('#FF9800', 0.3)}` }} />}
                {playerOut === existingSquad.viceCaptainId && <Chip label="VC" size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha(accent, 0.15), color: accent, border: `1px solid ${alpha(accent, 0.3)}` }} />}
                {playerOut === existingSquad.xFactorId && <Chip label="X" size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha('#9C27B0', 0.15), color: '#9C27B0', border: `1px solid ${alpha('#9C27B0', 0.3)}` }} />}
              </Box>
              {/* Arrow connector */}
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.25, bgcolor: alpha(accent, 0.04), borderLeft: `1px solid ${alpha(accent, 0.12)}`, borderRight: `1px solid ${alpha(accent, 0.12)}` }}>
                <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: alpha(accent, 0.15), border: `1px solid ${alpha(accent, 0.35)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ArrowForward sx={{ fontSize: 16, color: accent, transform: 'rotate(90deg)' }} />
                </Box>
              </Box>
              {/* IN row */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.25,
                px: 1.5, py: 1.25, borderRadius: '4px 4px 12px 12px',
                bgcolor: alpha(colors.success.primary, 0.07), border: `1px solid ${alpha(colors.success.primary, 0.18)}`,
                borderTop: 'none',
              }}>
                <Box sx={{ width: 28, height: 28, borderRadius: 1.25, bgcolor: alpha(colors.success.primary, 0.12), border: `1px solid ${alpha(colors.success.primary, 0.25)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '0.5rem', fontWeight: 800, color: colors.success.primary, textTransform: 'uppercase' }}>IN</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: colors.text.primary, fontSize: '0.9rem' }} noWrap>
                    {availablePlayers.find(p => p.id === playerIn)?.name || playerIn}
                  </Typography>
                  {(() => {
                    const p = availablePlayers.find(p => p.id === playerIn);
                    return p ? (
                      <Typography sx={{ fontSize: '0.65rem', color: alpha(colors.text.secondary, 0.55) }}>
                        {cricketFlagEmoji(p.team)}{cricketFlagEmoji(p.team) ? ' ' : ''}{p.team} · {p.role}
                      </Typography>
                    ) : null;
                  })()}
                </Box>
              </Box>
            </Box>
          ) : (
            // Role reassignment confirm
            <Box sx={{ mb: 2 }}>
              <Box sx={{
                p: 2, borderRadius: 2.5,
                bgcolor: alpha(accent, 0.06), border: `1px solid ${alpha(accent, 0.15)}`,
              }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 2, alignItems: 'center' }}>
                  {/* BEFORE */}
                  <Box>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: alpha(colors.text.secondary, 0.4), textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5, textAlign: 'center' }}>Before</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                      {(selectedTransferType === 'bench' || selectedTransferType === 'wildcard') && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: alpha('#FF9800', 0.08), borderRadius: 1.5, border: `1px solid ${alpha('#FF9800', 0.25)}` }}>
                          <Chip label="C" size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha('#FF9800', 0.2), color: '#FF9800' }} />
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.text.primary, flex: 1 }} noWrap>{getPlayerName(existingSquad.captainId)}</Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: alpha(accent, 0.08), borderRadius: 1.5, border: `1px solid ${alpha(accent, 0.25)}` }}>
                        <Chip label="VC" size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha(accent, 0.2), color: accent }} />
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.text.primary, flex: 1 }} noWrap>{getPlayerName(existingSquad.viceCaptainId)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: alpha('#9C27B0', 0.08), borderRadius: 1.5, border: `1px solid ${alpha('#9C27B0', 0.25)}` }}>
                        <Chip label="X" size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha('#9C27B0', 0.2), color: '#9C27B0' }} />
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.text.primary, flex: 1 }} noWrap>{getPlayerName(existingSquad.xFactorId)}</Typography>
                      </Box>
                    </Box>
                  </Box>
                  {/* Arrow */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: alpha(accent, 0.15), border: `1px solid ${alpha(accent, 0.35)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ArrowForward sx={{ fontSize: 16, color: accent }} />
                    </Box>
                  </Box>
                  {/* AFTER */}
                  <Box>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: alpha(colors.text.secondary, 0.4), textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5, textAlign: 'center' }}>After</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                      {(() => {
                        let newCaptainAfter = newCaptain || existingSquad.captainId;
                        let newVCAfter = newViceCaptain || existingSquad.viceCaptainId;
                        let newXAfter = newXFactor || existingSquad.xFactorId;

                        if (newCaptain) {
                          if (newCaptain === existingSquad.viceCaptainId) newVCAfter = existingSquad.captainId;
                          else if (newCaptain === existingSquad.xFactorId) newXAfter = existingSquad.captainId;
                        }
                        if (newViceCaptain) {
                          if (newViceCaptain === existingSquad.xFactorId) newXAfter = existingSquad.viceCaptainId;
                          if (newViceCaptain === existingSquad.captainId) newCaptainAfter = existingSquad.viceCaptainId;
                        }
                        if (newXFactor) {
                          if (newXFactor === existingSquad.viceCaptainId) newVCAfter = existingSquad.xFactorId;
                          if (newXFactor === existingSquad.captainId) newCaptainAfter = existingSquad.xFactorId;
                        }

                        return (
                          <>
                            {(selectedTransferType === 'bench' || selectedTransferType === 'wildcard') && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: alpha('#FF9800', newCaptain ? 0.15 : 0.08), borderRadius: 1.5, border: `${newCaptain ? 2 : 1}px solid ${newCaptain ? '#FF9800' : alpha('#FF9800', 0.25)}` }}>
                                <Chip label="C" size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha('#FF9800', 0.2), color: '#FF9800' }} />
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: newCaptain ? 700 : 600, color: colors.text.primary, flex: 1 }} noWrap>{getPlayerName(newCaptainAfter)}</Typography>
                                {newCaptain && <Chip label="NEW" size="small" sx={{ height: 14, fontSize: '0.58rem', fontWeight: 700, bgcolor: '#FF9800', color: 'white' }} />}
                              </Box>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: alpha(accent, newViceCaptain ? 0.15 : 0.08), borderRadius: 1.5, border: `${newViceCaptain ? 2 : 1}px solid ${newViceCaptain ? accent : alpha(accent, 0.25)}` }}>
                              <Chip label="VC" size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha(accent, 0.2), color: accent }} />
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: newViceCaptain ? 700 : 600, color: colors.text.primary, flex: 1 }} noWrap>{getPlayerName(newVCAfter)}</Typography>
                              {newViceCaptain && <Chip label="NEW" size="small" sx={{ height: 14, fontSize: '0.58rem', fontWeight: 700, bgcolor: accent, color: 'white' }} />}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: alpha('#9C27B0', newXFactor ? 0.15 : 0.08), borderRadius: 1.5, border: `${newXFactor ? 2 : 1}px solid ${newXFactor ? '#9C27B0' : alpha('#9C27B0', 0.25)}` }}>
                              <Chip label="X" size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha('#9C27B0', 0.2), color: '#9C27B0' }} />
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: newXFactor ? 700 : 600, color: colors.text.primary, flex: 1 }} noWrap>{getPlayerName(newXAfter)}</Typography>
                              {newXFactor && <Chip label="NEW" size="small" sx={{ height: 14, fontSize: '0.58rem', fontWeight: 700, bgcolor: '#9C27B0', color: 'white' }} />}
                            </Box>
                          </>
                        );
                      })()}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* Validate substitution */}
          {selectedChangeType === 'playerSubstitution' && (() => {
            const validation = validatePlayerSubstitution();
            if (validation) {
              return (
                <Box sx={{ px: 1.25, py: 1, borderRadius: 2, bgcolor: alpha('#EF4444', 0.09), border: `1px solid ${alpha('#EF4444', 0.25)}`, mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#EF4444', fontFamily: "'Satoshi', sans-serif" }}>⚠ {validation}</Typography>
                </Box>
              );
            }
            return null;
          })()}

          {/* Transfers remaining pill */}
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.6,
            px: 1.25, py: 0.5, borderRadius: 10,
            bgcolor: remainingAfter > 0 ? alpha(colors.success.primary, 0.09) : alpha('#EF4444', 0.1),
            border: `1px solid ${remainingAfter > 0 ? alpha(colors.success.primary, 0.22) : alpha('#EF4444', 0.28)}`,
          }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: remainingAfter > 0 ? colors.success.primary : '#EF4444' }} />
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: remainingAfter > 0 ? colors.success.primary : '#EF4444', fontFamily: "'Satoshi', sans-serif" }}>
              {remainingAfter > 0
                ? `${remainingAfter} ${selectedTransferType} transfer${remainingAfter !== 1 ? 's' : ''} remaining after this`
                : 'This is your last transfer of this type'
              }
            </Typography>
          </Box>

          {error && (
            <Box sx={{ px: 1.25, py: 1, borderRadius: 2, bgcolor: alpha('#EF4444', 0.09), border: `1px solid ${alpha('#EF4444', 0.25)}`, mt: 1.5 }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#EF4444', fontFamily: "'Satoshi', sans-serif" }}>{error}</Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 3, sm: 2.5 }, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: 1 }}>
          <Button variant="text"
            onClick={handleBack}
            disabled={submitting}
            sx={{ flex: 1, color: alpha(colors.text.secondary, 0.6), textTransform: 'none', fontFamily: "'Satoshi', sans-serif", borderRadius: 2 }}>
            ← Back
          </Button>
          <Button variant="contained"
            onClick={handleSubmit}
            disabled={submitting || (selectedChangeType === 'playerSubstitution' && !!validatePlayerSubstitution())}
            startIcon={submitting ? <CircularProgress size={15} color="inherit" sx={{ mr: -0.25 }} /> : <CheckCircle sx={{ fontSize: 17 }} />}
            sx={{ flex: 2.5, ...ctaButton(submitting || (selectedChangeType === 'playerSubstitution' && !!validatePlayerSubstitution())) }}>
            {submitting ? 'Submitting…' : 'Confirm Transfer'}
          </Button>
        </Box>
      </>
    );
  };

  // ── Dialog ─────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          bgcolor: '#080D18',
          backgroundImage: 'none',
          border: { xs: 'none', sm: `1px solid ${alpha(accent, 0.2)}` },
          borderRadius: { xs: '20px 20px 0 0', sm: 3 },
          m: { xs: 0, sm: 2 },
          mt: { xs: 'auto', sm: 2 },
          maxHeight: { xs: '92vh', sm: '86vh' },
          display: 'flex',
          flexDirection: 'column',
          '&::before': {
            content: { xs: '""', sm: 'none' },
            display: 'block',
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.15)',
            mx: 'auto',
            mt: 1,
            mb: -0.5,
            flexShrink: 0,
          },
        },
      }}
    >
      {activeStep === 0 && renderStepType()}
      {activeStep === 1 && subStep === 'changeType' && renderStepChangeType()}
      {activeStep === 1 && subStep === 'playerOut' && renderStepPlayerOut()}
      {activeStep === 1 && subStep === 'playerIn' && renderStepPlayerIn()}
      {activeStep === 1 && subStep === 'roleReassign' && renderStepRoleReassign()}
      {activeStep === 2 && renderStepConfirm()}
    </Dialog>
  );
};

export default TransferModal;
